import { isEmployedAtEndDate } from '../../../packages/@treams/treams-analytics/src/filters/EmploymentFilter';
import { extractLocalizedText } from '../../../packages/@treams/treams-analytics/src/helpers';

export type RewardUser = {
	userID: string;
	firstName?: string;
	lastName?: string;
	isArchived?: number;
	excludeAnalytics?: boolean | null;
	activatedAt?: Date | null;
	deactivatedAt?: Date | null;
	archivedAt?: Date | null;
	/** Only present in the full-access snapshot. */
	gender?: string;
};

export type RewardPosition = {
	userID?: string;
	jobTitleID?: string;
	status?: string;
	isArchived?: number;
	startDate?: Date | null;
	endDate?: Date | null;
};

export type RewardTitle = {
	jobTitleID: string;
	title?: unknown;
	salaryScaleID?: string | null;
};

export type RewardScale = {
	salaryScaleID: string;
	name?: string;
	startAmount?: number;
	endAmount?: number;
};

export type RewardChange = {
	userID?: string;
	/** Full-time salary. */
	amount?: number;
	/** Hours per week. */
	fteHours?: number;
	reason?: string;
	createdAt?: Date | null;
};

export type RewardRange = {
	/** Name of the salary scale, for example "Schaal 8" or "Junior". */
	name?: string;
	startAmount?: number;
	endAmount?: number;
	/** Full-time salary, or the function average. */
	amount?: number;
	/** Hours per week for this person. Absent on a function average. */
	fteHours?: number;
};

export type RewardEmployee = {
	id: string;
	name: string;
	firstName: string;
	lastName: string;
	/** Full-time salary. Set even when the function has no scale to place it on. */
	salary?: number;
	fteHours?: number;
	range?: RewardRange;
};

export type RewardFunction = {
	id: string;
	name: string;
	range?: RewardRange;
	employees: RewardEmployee[];
};

const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;

export function positionInRange(
	amount: number,
	startAmount: number,
	endAmount: number,
): number | undefined {
	if (endAmount <= startAmount) {
		return undefined;
	}

	return (amount - startAmount) / (endAmount - startAmount);
}

export type SalaryDomain = { min: number; max: number };

const SALARY_DOMAIN_STEP = 1_000;

// One axis for every row, taken from the scales on the page. Salaries that sit
// close to those scales widen it; a salary far outside stays pinned at the edge.
// Without scales the salaries themselves set the axis. Bounds snap outward to
// the next thousand so a scale does not sit flush against an end tick.
export function salaryDomain(rows: RewardFunction[]): SalaryDomain {
	const scaleAmounts: number[] = [];
	const salaries: number[] = [];

	for (const row of rows) {
		if (row.range?.startAmount != null) scaleAmounts.push(row.range.startAmount);
		if (row.range?.endAmount != null) scaleAmounts.push(row.range.endAmount);

		for (const employee of row.employees) {
			if (employee.salary != null) salaries.push(employee.salary);
		}
	}

	const anchors = scaleAmounts.length > 0 ? scaleAmounts : salaries;

	if (anchors.length === 0) {
		return { min: 0, max: SALARY_DOMAIN_STEP };
	}

	const anchorMin = Math.min(...anchors);
	const anchorMax = Math.max(...anchors);
	const reach = Math.max((anchorMax - anchorMin) * 0.5, SALARY_DOMAIN_STEP);
	const included = [
		...anchors,
		...salaries.filter((salary) => salary >= anchorMin - reach && salary <= anchorMax + reach),
	];
	const low = Math.min(...included);
	const high = Math.max(...included);

	let min = Math.floor(low / SALARY_DOMAIN_STEP) * SALARY_DOMAIN_STEP;
	let max = Math.ceil(high / SALARY_DOMAIN_STEP) * SALARY_DOMAIN_STEP;

	if (min === low) min -= SALARY_DOMAIN_STEP;
	if (max === high) max += SALARY_DOMAIN_STEP;

	min = Math.max(0, min);

	if (max <= min) max = min + SALARY_DOMAIN_STEP;

	return { min, max };
}

export function personName(user: RewardUser): string {
	return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
}

// Older positions have no `status`, so being current follows the backend:
// not archived, and today falls between start and end.
function isCurrentPosition(position: RewardPosition, now: Date): boolean {
	if (position.isArchived === 1) {
		return false;
	}

	if (position.startDate && position.startDate > now) {
		return false;
	}

	if (position.endDate && position.endDate <= now) {
		return false;
	}

	return true;
}

export function currentPositions(
	positions: RewardPosition[],
	now: Date,
): Map<string, RewardPosition> {
	const byUser = new Map<string, RewardPosition>();

	for (const position of positions) {
		if (!position.userID || !isCurrentPosition(position, now)) {
			continue;
		}

		const current = byUser.get(position.userID);
		const start = position.startDate?.getTime() ?? 0;
		const currentStart = current?.startDate?.getTime() ?? 0;

		if (!current || start >= currentStart) {
			byUser.set(position.userID, position);
		}
	}

	return byUser;
}

// Newest non-bonus salary-development row per person. That amount is the full-time salary.
export function currentSalaries(
	changes: RewardChange[],
): Map<string, { amount: number; fteHours?: number }> {
	const latest = new Map<string, { amount: number; fteHours?: number; createdAt: number }>();

	for (const change of changes) {
		if (!change.userID || typeof change.amount !== 'number' || change.reason === 'ONE_OFF_BONUS') {
			continue;
		}

		const createdAt = change.createdAt?.getTime() ?? 0;
		const current = latest.get(change.userID);

		if (!current || createdAt >= current.createdAt) {
			latest.set(change.userID, {
				amount: change.amount,
				fteHours: change.fteHours,
				createdAt,
			});
		}
	}

	return new Map(
		[...latest].map(([userID, change]) => [
			userID,
			{ amount: change.amount, fteHours: change.fteHours },
		]),
	);
}

export function isRewardEmployee(user: RewardUser, now: Date, viewerID?: string): boolean {
	return user.userID !== viewerID && user.isArchived !== 1 && isEmployedAtEndDate(user, now);
}

function rangeFor(
	amount: number | undefined,
	scale: RewardScale | undefined,
	fteHours: number | undefined,
): RewardRange | undefined {
	if (!scale || typeof scale.startAmount !== 'number' || typeof scale.endAmount !== 'number') {
		return undefined;
	}

	return {
		name: scale.name,
		startAmount: scale.startAmount,
		endAmount: scale.endAmount,
		amount,
		fteHours,
	};
}

// Everyone in a function shares its scale. The row shows those bounds; the amount is their average salary.
function functionRange(
	scale: RewardScale | undefined,
	employees: RewardEmployee[],
): RewardRange | undefined {
	const salaries = employees.flatMap((employee) =>
		employee.salary == null ? [] : [employee.salary],
	);

	if (!scale) {
		return undefined;
	}

	return rangeFor(salaries.length === 0 ? undefined : average(salaries), scale, undefined);
}

export function buildRewardFunctions({
	users,
	positions,
	titles,
	scales,
	changes,
	now,
	language,
	noFunctionName,
	viewerID,
}: {
	users: RewardUser[];
	positions: RewardPosition[];
	titles: RewardTitle[];
	scales: RewardScale[];
	changes: RewardChange[];
	now: Date;
	language: string;
	noFunctionName: string;
	viewerID?: string;
}): RewardFunction[] {
	const titlesByID = new Map(titles.map((title) => [title.jobTitleID, title]));
	const scalesByID = new Map(scales.map((scale) => [scale.salaryScaleID, scale]));
	const scaleOf = (title: RewardTitle | undefined) =>
		title?.salaryScaleID ? scalesByID.get(title.salaryScaleID) : undefined;
	const positionByUser = currentPositions(positions, now);
	const salaryByUser = currentSalaries(changes);
	const rows = new Map<string, RewardEmployee[]>();

	for (const user of users) {
		if (!isRewardEmployee(user, now, viewerID)) {
			continue;
		}

		const jobTitleID = positionByUser.get(user.userID)?.jobTitleID;
		const title = jobTitleID ? titlesByID.get(jobTitleID) : undefined;
		const functionID = title?.jobTitleID ?? '';
		const salary = salaryByUser.get(user.userID);

		rows.set(functionID, [
			...(rows.get(functionID) ?? []),
			{
				id: user.userID,
				name: personName(user) || user.userID,
				firstName: user.firstName ?? '',
				lastName: user.lastName ?? '',
				salary: salary?.amount,
				fteHours: salary?.fteHours,
				range: rangeFor(salary?.amount, scaleOf(title), salary?.fteHours),
			},
		]);
	}

	return [...rows.entries()]
		.map(([id, employees]) => {
			const title = id ? titlesByID.get(id) : undefined;

			return {
				id: id || 'none',
				name: (title && extractLocalizedText(title.title, language)) || noFunctionName,
				range: functionRange(scaleOf(title), employees),
				employees: employees.sort((left, right) => left.name.localeCompare(right.name, language)),
			};
		})
		.sort((left, right) => compareRewardFunctions(left, right, 'name', language));
}

export function peopleOutsideScale(row: RewardFunction): number {
	const start = row.range?.startAmount;
	const end = row.range?.endAmount;

	if (start == null || end == null) {
		return 0;
	}

	return row.employees.filter(
		(employee) => employee.salary != null && (employee.salary < start || employee.salary > end),
	).length;
}

// People outside the scale count first. Distance from the middle breaks ties.
export function scaleDeviation(row: RewardFunction): number {
	const start = row.range?.startAmount;
	const end = row.range?.endAmount;

	if (start == null || end == null) {
		return 0;
	}

	const outside = peopleOutsideScale(row);
	const span = Math.max(end - start, 1);
	const middle = (start + end) / 2;
	const furthest = row.employees.reduce((max, employee) => {
		if (employee.salary == null) {
			return max;
		}

		return Math.max(max, Math.abs(employee.salary - middle) / span);
	}, 0);

	return outside * 1000 + furthest;
}

export type RewardSort = 'name' | 'deviation' | 'salary-desc' | 'salary-asc';

export function averageSalary(amounts: Array<number | undefined>): number | undefined {
	const salaries = amounts.filter((amount): amount is number => amount != null);

	if (salaries.length === 0) {
		return undefined;
	}

	return salaries.reduce((sum, amount) => sum + amount, 0) / salaries.length;
}

export function compareRewardFunctions(
	left: RewardFunction,
	right: RewardFunction,
	sort: RewardSort,
	language: string,
) {
	if (left.id === 'none') return 1;
	if (right.id === 'none') return -1;

	if (sort === 'deviation') {
		const difference = scaleDeviation(right) - scaleDeviation(left);

		if (difference !== 0) {
			return difference;
		}
	}

	if (sort === 'salary-desc' || sort === 'salary-asc') {
		const leftSalary = averageSalary(left.employees.map((employee) => employee.salary));
		const rightSalary = averageSalary(right.employees.map((employee) => employee.salary));

		if (leftSalary == null && rightSalary != null) return 1;
		if (leftSalary != null && rightSalary == null) return -1;

		if (leftSalary != null && rightSalary != null && leftSalary !== rightSalary) {
			return sort === 'salary-desc' ? rightSalary - leftSalary : leftSalary - rightSalary;
		}
	}

	return left.name.localeCompare(right.name, language);
}
